/**
 * Everything the agent needs from the outside world, as interfaces. The app implements them with Capacitor plugins;
 * tests implement them in memory. Nothing in this package may import a browser, Node or Capacitor API directly, so
 * the core keeps running if the shell around it changes.
 */
/**
 * Every storage collection the agent uses. One name per kind of record, so two features never share one by accident.
 * Each belongs to one owner (the person signed in), except the few kept per phone (`PHONE_COLLECTIONS`, owner/storage.ts).
 */
export const Collection = {
    /** Talk's chats, one record per chat, kept until the owner deletes one (chat/store.ts). */
    chat: "chat",
    /** A household's Find chats, one record per chat with its need, kept until the owner deletes one (chat/store.ts). */
    findChat: "find_chat",
    contacts: "contacts",
    engagements: "engagements",
    /** One record per job this device is part of (as consumer or worker). */
    jobs: "jobs",
    /** What a consumer is looking for, per request group: the need and their private budget. */
    needs: "needs",
    /** Decisions waiting for the owner: confirm a deal, answer questions, confirm a payment. */
    tasks: "tasks",
    /** Payment requests received, with the worker's UPI ID, so the consumer can open a UPI app. */
    paymentRequests: "payment_requests",
    episodes: "episodes",
    inboxCursor: "inbox_cursor",
    inboxSeen: "inbox_seen",
    outbox: "outbox",
    outboxSeq: "outbox_seq",
    payments: "payments",
    /** Settings of the phone itself, shared by everyone who signs in on it: the account, language, model choice. */
    settings: "settings",
    /** Settings of one owner: their UPI ID, whether the agent asks before answering from memory. */
    ownerSettings: "owner_settings",
    /** Names a consumer saw on the board, by user id, so their jobs show who they are talking to. */
    people: "people",
    /**
     * Live mentor links, one record per person. Kept under the name links had when workers also linked in person, so
     * links opened before that went away still read.
     */
    mentorLinks: "colleagues",
    /** The notes on one mentor link, one document per person; under their old name for the same reason. */
    linkNotes: "colleague_notes",
    /** Mentor links that ran out or were ended: who this owner knew, so they can still introduce them. */
    pastLinks: "past_links",
    /** What the agent remembers from past signed jobs, one document per owner; kept when they sign out. */
    memory: "memory",
    /** Landing requests this phone sent as a newcomer, one per mentor asked. */
    mentorRequests: "mentor_requests",
    /** Landing requests this phone received as a mentor. */
    mentorAsks: "mentor_asks",
    /** Introductions toward a mentor link this phone is one of the two sides of. */
    mentorIntros: "mentor_intros",
    /** Answers to an introduction that came before the introduction itself, until it arrives. */
    mentorEarly: "mentor_early",
    /** The owner's own mentor choices and newcomer answers, kept on this phone only. */
    mentorSettings: "mentor_settings",
    /** People this owner blocked: hidden everywhere this owner looks on this phone. */
    blocked: "blocked",
    /** Moves to a new city, one record per move, kept on this phone (the landing journey). */
    journeys: "journeys",
    /** City guides as last downloaded, one per city, so the landing journey works offline. */
    guides: "guides",
    /** Offers to work this household received from a worker who scanned its QR (ADR 0034), one per channel. */
    workOffers: "work_offers",
    /** Offers to work this worker sent, one per channel: the day's count and "offered" on a household's view. */
    offersSent: "offers_sent",
    /** ADR 0045: a household's saved addresses, one record each; they never leave the phone except to a new one. */
    addresses: "addresses",
    /** ADR 0048: a worker's own bookings, one record each; they never leave the phone except to a new one. */
    bookings: "bookings",
    /** What GigSync accepted of this owner's job records (ADR 0038), one record per fact: what is still to send is the rest. */
    recordsSent: "records_sent",
};
/** Reply length when a caller does not ask for one: enough for a chat answer, short enough for a phone. */
export const DEFAULT_MAX_TOKENS = 256;
/** In-memory implementations for tests and the web preview. */
export class MemoryStorage {
    data = new Map();
    bucket(c) {
        const existing = this.data.get(c);
        if (existing)
            return existing;
        const fresh = new Map();
        this.data.set(c, fresh);
        return fresh;
    }
    async get(c, id) {
        return this.bucket(c).get(id);
    }
    async put(c, id, v) {
        this.bucket(c).set(id, v);
    }
    async delete(c, id) {
        this.bucket(c).delete(id);
    }
    async list(c) {
        return [...this.bucket(c).values()];
    }
    async ids(c) {
        return [...this.bucket(c).keys()];
    }
}
export class NoModel {
    async generate() {
        return null;
    }
}
export class NoEmbedder {
    async embed() {
        return null;
    }
}
export class SystemClock {
    now() {
        return new Date();
    }
}
//# sourceMappingURL=ports.js.map